import { Injectable } from '@angular/core';

import { AppConfig } from 'src/shared/providers/config';
import { Events } from 'src/providers/events/events';
import { Logger } from 'src/shared/providers/logger';
import { HttpClient } from 'src/shared/providers/http/http-client';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { PositionModel } from 'src/shared/models/position.model';

import { Geolocation, PositionOptions } from '@capacitor/geolocation';

import * as geolib from 'geolib';
import { DateTime as LuxonDateTime } from 'luxon';
declare const google: any;
declare const window: any;

interface DistanceToData {
  distance: {
    text?: string,
    value?: number
  },
  duration: {
    text?: string,
    value?: number,
    withMargin?: string
  }
};

const defaultOptions: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 50000
};

@Injectable({
  providedIn: 'root'
})

export class Location {

  constructor(
    private events: Events,
    private httpClient: HttpClient,
    private logger: Logger,
    private storage: TidyStorage
  ) {
    this.get(true);
    setInterval(() => {
      this.get(true);
    }, 60000);
  }

  public async get(trueLocation: boolean = false): Promise<PositionModel> {
    let cachedLocation: PositionModel;
    try {
      cachedLocation = await this.storage.retrieve('location');
      const locationPermission = await Geolocation.checkPermissions();
      if (locationPermission.location === 'denied') {
        return this.returnNoLocation({message: 'Location permission not granted'}, cachedLocation);
      }
      const secondsSinceLastCache = await this.getSecondsSinceLastCache();
      if (trueLocation || !cachedLocation || secondsSinceLastCache > 180) {
        const location = await this.withFailTimeout(this.getLocationFromCapacitor(defaultOptions));
        await this.storage.save('locationTimeStamp', LuxonDateTime.now());
        await this.storage.save('location', location);
        return location;
      }
      return cachedLocation;
    } catch(err) {
      // A live read failed: fall back to the last known-good cached fix rather than 0,0.
      return this.getLowAccuracy(cachedLocation);
    }
  }

  public async getLowAccuracy(cachedLocation?: PositionModel): Promise<PositionModel> {
    try {
      const options = {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 50000
      };
      const location = await this.withFailTimeout(
        this.getLocationFromCapacitor(options),
        {message: 'fail safe timeout triggered'}
      );
      await this.storage.save('locationTimeStamp', LuxonDateTime.now());
      await this.storage.save('location', location);
      return location;
    } catch(err) {
      return this.returnNoLocation(err, cachedLocation);
    }
  }

  // Races a location lookup against a 15s fail-safe timeout, always clearing the
  // timer (via finally) so it can never reject() after the lookup already settled.
  // The previous dangling-timeout + async-Promise-executor pattern was the source
  // of "Uncaught (in promise): GeolocationPositionError: {}" — the #1 pro-app Sentry error.
  private withFailTimeout(
    locationPromise: Promise<PositionModel>,
    timeoutError: any = {message: 'could not obtain location in time'}
  ): Promise<PositionModel> {
    let failTimeout;
    const timeout = new Promise<PositionModel>((_resolve, reject) => {
      failTimeout = setTimeout(() => reject(timeoutError), 15000);
    });
    return Promise.race([locationPromise, timeout]).finally(() => clearTimeout(failTimeout));
  }

  async getLocationFromCapacitor(options) {
    const currentLocation = await Geolocation.getCurrentPosition(options);
    return {
      latitude: currentLocation.coords.latitude,
      longitude: currentLocation.coords.longitude
    };
  }

  async getSecondsSinceLastCache() {
    const locationTimeStamp = await this.storage.retrieve('locationTimeStamp');
    const luxonLocationTimeStamp = LuxonDateTime.fromISO(locationTimeStamp);
    const luxonCurrentTime = LuxonDateTime.now();
    const timeSinceLastCache = luxonCurrentTime.diff(luxonLocationTimeStamp, ["seconds"]);
    const timeSinceLastCacheObject = timeSinceLastCache.toObject();
    return timeSinceLastCacheObject.seconds;
  }

  private async returnNoLocation(err, cachedLocation?: PositionModel): Promise<PositionModel> {
    const error = err.message ? err.message : 'Error acquiring user location';
    this.logger.error(err, `Get position error: ${error}`);
    // A failed live read is "location unknown", not a real 0,0 position (0,0 is the
    // Gulf of Guinea, never a Pro/client location). Prefer the last known-good cached
    // fix so callers (distance checks, moments/map) get real coordinates instead of a
    // pin in the Atlantic, and keep the error flag so they/the backend still know the
    // live reading failed. Also never persist this over the cache or restamp the
    // timestamp — that poisoned freshness and made the finish gate report ~12,583 km.
    // See Trello #18586.
    if (this.isValidPosition(cachedLocation)) {
      return { ...cachedLocation, error };
    }
    return { latitude: 0, longitude: 0, error };
  }

  // A failed reading resolves to the {0, 0} sentinel (with an error attached) rather
  // than rejecting, so callers must guard on validity before using the coordinates.
  // 0,0 is a real point in the Gulf of Guinea, never a Pro/client location.
  public isValidPosition(position: PositionModel): boolean {
    if (!position || position.error) {
      return false;
    }
    return position.latitude !== 0 || position.longitude !== 0;
  }

  public async distanceTo(address: string): Promise<DistanceToData> {
    try {
      const position = await this.get();
      const data = await this.fetchDistanceFromGoogle(position, address);
      return this.parseGoogleData(data);
    } catch (err) {
      this.logger.error(err);
      return { distance: {}, duration: {} };
    }
  }

  public kmDistanceBetween(source: PositionModel, destiny: PositionModel): number {
    const metersDistance = geolib.getDistance(source, destiny);
    return geolib.convertDistance(metersDistance, 'km');
  }

  public loadIsochroneMapbox(profile, longitude, latitude, minutesDistance) {
    const base = 'https://api.mapbox.com/isochrone/v1/mapbox';
    const coordinates = `${longitude},${latitude}`;
    const accessToken = AppConfig.MAPBOX_KEY;
    // Mapbox documentation allows a maximum value of 60min
    const countorMinutes = minutesDistance > 60 ? 60 : minutesDistance;
    const url = `${base}/${profile}/${coordinates}.json?contours_minutes=${countorMinutes}&access_token=${accessToken}`;

    return this.httpClient.get(url);
  }

  private fetchDistanceFromGoogle(position, destiny): Promise<google.maps.DistanceMatrixResponse> {
    return new Promise( resolve => {
      new google.maps.DistanceMatrixService().getDistanceMatrix({
        origins: [`${position.latitude},${position.longitude}`],
        destinations: [destiny],
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.IMPERIAL
      }, resolve);
    });
  }

  private parseGoogleData(data: google.maps.DistanceMatrixResponse): DistanceToData {
    const result = data?.rows?.at(0)?.elements?.at(0);

    if (!result || result.status !== 'OK') {
      return { distance: {}, duration: {} };
    }

    return {
      distance: result.distance,
      duration: {
        text: result.duration.text,
        value: result.duration.value,
        withMargin: this.calculateDurationWithTolerance(result.duration.value)
      }
    };
  }

  private calculateDurationWithTolerance(seconds: number): string {
    const minutesWithTolerance = Math.ceil( (seconds / 60) * 1.1);

    const minutesText = minutesWithTolerance > 1 ? minutesWithTolerance + ' minutes' : minutesWithTolerance + ' minute';

    return minutesText;
  }

  throwErrorMessage() {
    const errorMsg = `We aren't getting your location!  Its important for clients to get accurate updates during cleanings.  Please check your settings.`;
    this.events.publish('message:show', {messageStr: errorMsg, styleClass: 'error'});
  }
  
}
